import { TextStyles, Styles, LinearLayoutType, FrameType } from "./types";
import { PrefixWith$ } from "../types";

function getTextCase(textCase: string) {
  if (textCase === `UPPER`) {
    return `uppercase`;
  }

  if (textCase === `CAPITAL`) {
    return `capitalize`;
  }

  return `normal`;
}

/**
 * Returns styles for Label nodes.
 */
export function getStyledTextAttributes({
  fillColor,
  fontFamily,
  fontWeight,
  textDecoration,
  letterSpacing,
  lineHeight,
  size,
  textCase,
}: TextStyles & Styles = {}) {
  return `
    ${fillColor ? `color: ${fillColor};` : ``}
    ${fontFamily ? `font-family: ${fontFamily};` : ``}
    ${size ? `font-size: ${size / 16}rem;` : ``}
    ${lineHeight ? `line-height: ${lineHeight}px;` : ``}
    ${textDecoration ? `text-decoration: ${textDecoration};` : ``}
    ${letterSpacing ? `letter-spacing: ${letterSpacing}px;` : ``}
    ${textCase ? `text-transform: ${getTextCase(textCase)};` : ``}
    ${fontWeight ? `font-weight: ${fontWeight};` : ``}
    margin: 0;
  `;
}

/**
 * Returns styles for nodes that support theming,
 * like Buttons and Views.
 */
export function getStyledNodeAttributes(
  { boxShadow, cornerRadius, fillColor, stroke }: Styles = {},
  isSeparator = false,
) {
  return `
    ${
      boxShadow
        ? `box-shadow: ${boxShadow.offset.x}px ${boxShadow.offset.y}px ${
            boxShadow.radius
          }px ${boxShadow.spread || 0}px ${boxShadow.color}`
        : ``
    };
    ${cornerRadius ? `border-radius: ${cornerRadius}px;` : ``}
    ${fillColor ? `background: ${fillColor};` : ``}
    ${
      stroke
        ? `border${isSeparator ? `-bottom` : ``}: ${stroke.weight}px ${
            stroke.type
          } ${stroke.color}`
        : ``
    };
  `;
}

/**
 * Returns styles for nodes that support frame attributes,
 * like Views or Linear Layouts.
 */
export function getFrameAttributes({ width, height, padding }: FrameType = {}) {
  return `
  // Dimensions
  ${width?.resize === `fixed` ? `width: 100%;` : ``}
  ${width?.resize === `fixed` ? `height: 100%;` : ``}
  ${width?.resize === `fixed` && width.min ? `min-width: ${width.min}px` : ``};
  ${
    height?.resize === `fixed` && height.min
      ? `min-height: ${height.min}px`
      : ``
  };
  ${width?.resize === `fixed` && width.max ? `max-width: ${width.max}px` : ``};
  ${
    height?.resize === `fixed` && height.max
      ? `max-height: ${height.max}px`
      : ``
  };

  // Frame
  padding-top: ${padding?.top || 0}px;
  padding-right: ${padding?.right || 0}px;
  padding-bottom: ${padding?.bottom || 0}px;
  padding-left: ${padding?.left || 0}px;

  [data-direction="horizontal"] > && {
    ${width?.resize === `fill` ? `flex: 1;` : ``}
    ${height?.resize === `fill` ? `align-self: stretch;` : ``}
    ${
      width?.resize !== `fill`
        ? `min-width: ${width?.min || width?.max || 0}px;`
        : ``
    }
  }

  [data-direction="vertical"] > && {
    ${height?.resize === `fill` ? `flex: 1;` : ``}
    ${width?.resize === `fill` ? `width: 100%;` : ``}
  }
`;
}

/**
 * Returns the secondary axis of a node.
 */
export function getSecondaryAxis(direction: LinearLayoutType["direction"]) {
  return direction === `horizontal` ? `vertical` : `horizontal`;
}

/** Returns alignment of an axis. */
export function getAlignment(
  alignment: LinearLayoutType["alignment"],
  direction: NonNullable<LinearLayoutType["direction"]>,
) {
  // Fixme: Type error, doesnt exist...
  // : React.CSSProperties["alignItems"] {
  const align = alignment?.[direction];

  if (align === `center`) return `center`;

  if (align === `fill`) return `stretch`;

  if (align === `end`) return `flex-end`;

  return `flex-start`;
}

/** Returns justification. */
export function getJustification(
  distribution: LinearLayoutType["distribution"],
  direction: NonNullable<LinearLayoutType["direction"]>,
  alignment: LinearLayoutType["alignment"],
) {
  // Fixme: Type error, doesnt exist...
  // : React.CSSProperties["justifyContent"] {
  if (distribution === `equalAround`) return `space-around`;

  if (distribution === `equalSpacing`) return `space-between`;

  if (distribution === `fillEqually`) return `stretch; > * { flex: 1; }`;

  // If the distribution is fillProportionately,
  // justification is based on the alignment
  // of the primary axis. E.g. if a horizontal
  // layout has an "end" horizontal alignment,
  // its justification should be flex end.
  return getAlignment(alignment, direction);
}

/** Maps a prop object to an identically structured object where each key is prefixed with $ for use with styled-components transient props. */
export const prefixWith$ = <T extends Record<string, unknown>>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj || {}).map(([key, value]) => [
      key.startsWith(`on`) ? key : `$${key}`,
      value,
    ]),
  ) as PrefixWith$<T>;
